import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import GeoLocationCapture from '../components/GeoLocationCapture';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Trash2,
  ShieldCheck,
  Clock,
  LogIn,
  LogOut,
  Loader2,
  MapPin,
  Route,
  CheckCircle,
  AlertTriangle,
  Home,
  Smartphone,
  Receipt,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// KEYNOTE 23/24: the device lock is gone, but a handset swap must alert the admin.
// A stable per-browser id stands in for the handset identity.
const DEVICE_KEY = 'psm_device_id';

const getDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = `DEV-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return null; // private mode / storage blocked - punch still works, just unidentified
  }
};

const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '--:--';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const Attendance = () => {
  const { api, user } = useAuth();
  const isSuperAdmin = Boolean(user?.is_superadmin);
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [teamToday, setTeamToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [location, setLocation] = useState(null);
  // { row, kind: 'in' | 'out' } while the remove-punch dialog is open
  const [removal, setRemoval] = useState(null);
  const [removalReason, setRemovalReason] = useState('');
  const [removing, setRemoving] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  // The day the super admin is correcting; empty until today's date is known.
  const [teamDate, setTeamDate] = useState('');
  // Past days only: the corrected punch out time, HH:MM IST
  const [correctedOut, setCorrectedOut] = useState('');
  // The day whose TADA is being removed or restored, while that dialog is open
  const [tadaRow, setTadaRow] = useState(null);
  const [tadaReason, setTadaReason] = useState('');
  const [tadaSaving, setTadaSaving] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeam = async (date) => {
    try {
      const { data } = await api.get('/attendance', { params: { from_date: date, to_date: date } });
      setTeamToday(data);
    } catch (error) {
      toast.error('Failed to load attendance for that day');
    }
  };

  const fetchAll = async () => {
    try {
      const [todayRes, historyRes] = await Promise.all([
        api.get('/attendance/today'),
        api.get('/attendance')
      ]);
      setToday(todayRes.data);
      setHistory(historyRes.data);
      if (isSuperAdmin) {
        const date = teamDate || todayRes.data.date;
        setTeamDate(date);
        await fetchTeam(date);
      }
    } catch (error) {
      console.error('Failed to load attendance:', error);
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const punch = async (kind) => {
    if (!location) {
      toast.error('Capture your location first — punch in and punch out are geo-tagged');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/attendance/${kind}`, {
        geo_location: { lat: location.lat, lng: location.lng },
        device_id: getDeviceId()
      });
      toast.success(data.message);
      if (data.device_alert) toast.warning(data.device_alert);
      if (kind === 'punch-out') {
        toast.info(`Journey ${data.distance_km} km — ${data.travel_type}`);
      }
      setLocation(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${kind.replace('-', ' ')}`);
    } finally {
      setSubmitting(false);
    }
  };

  const toIstHHMM = (iso) =>
    iso ? new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata'
    }) : '';

  const openRemoval = (row, kind) => {
    setRemovalReason('');
    setCorrectedOut(toIstHHMM(row.punch_out?.time));
    setRemoval({ row, kind });
  };

  // A past day cannot be punched again, so its punch out is corrected instead.
  const correctingPastOut = removal?.kind === 'out' && removal?.row.date !== today?.date;

  const confirmRemoval = async () => {
    if (correctingPastOut && !correctedOut) {
      toast.error('Enter the correct punch out time');
      return;
    }
    setRemoving(true);
    try {
      const { data } = await api.post(`/attendance/${removal.row.id}/remove-punch`, {
        kind: removal.kind,
        reason: removalReason,
        ...(correctingPastOut ? { punch_out_time: correctedOut } : {})
      });
      toast.success(data.message);
      if (data.removed_tada_claim) {
        toast.info(`TADA claim ${data.removed_tada_claim} withdrawn - it is raised again at punch out`);
      }
      setRemoval(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove the punch');
    } finally {
      setRemoving(false);
    }
  };

  const openTada = (row) => {
    setTadaReason('');
    setTadaRow(row);
  };

  const confirmTada = async () => {
    setTadaSaving(true);
    try {
      const { data } = await api.post(`/attendance/${tadaRow.id}/tada`, {
        exclude: !tadaRow.tada_excluded,
        reason: tadaReason,
      });
      toast.success(data.message);
      setTadaRow(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change TADA for this day');
    } finally {
      setTadaSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  const punchedIn = today?.punched_in;
  const punchedOut = today?.punched_out;
  const dayComplete = punchedIn && punchedOut;
  const nextAction = !punchedIn ? 'punch-in' : !punchedOut ? 'punch-out' : null;

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="attendance-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attendance</h1>
          <p className="text-sm text-slate-500">
            Punch in from home before leaving, punch out after reaching back
          </p>
        </div>

        {/* Today's punch state */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-500">
              {formatDate(today?.date)}
            </span>
            <span className={cn(
              'px-2.5 py-1 rounded-full text-xs font-medium',
              dayComplete ? 'bg-emerald-100 text-emerald-700'
                : punchedIn ? 'bg-sky-100 text-sky-700'
                  : 'bg-amber-100 text-amber-700'
            )}>
              {dayComplete ? 'Day complete' : punchedIn ? 'In the market' : 'Not started'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className={cn(
              'rounded-xl p-4 border',
              punchedIn ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
            )}>
              <div className="flex items-center gap-2 mb-1">
                <LogIn className={cn('w-4 h-4', punchedIn ? 'text-emerald-600' : 'text-slate-400')} />
                <span className="text-xs font-medium text-slate-600">Punch In</span>
              </div>
              <p className="text-xl font-bold text-slate-900">
                {formatTime(today?.attendance?.punch_in?.time)}
              </p>
            </div>
            <div className={cn(
              'rounded-xl p-4 border',
              punchedOut ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
            )}>
              <div className="flex items-center gap-2 mb-1">
                <LogOut className={cn('w-4 h-4', punchedOut ? 'text-emerald-600' : 'text-slate-400')} />
                <span className="text-xs font-medium text-slate-600">Punch Out</span>
              </div>
              <p className="text-xl font-bold text-slate-900">
                {formatTime(today?.attendance?.punch_out?.time)}
              </p>
            </div>
          </div>

          {/* Live journey summary */}
          {punchedIn && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-sky-800">
                  <Route className="w-4 h-4" /> Distance travelled
                </span>
                <span className="font-semibold text-sky-900">{today.distance_km} km</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-sky-800">Claimable so far</span>
                <span className="font-semibold text-sky-900">
                  {today.billable_distance_km} km · {today.travel_type || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-sky-800">Visits approved</span>
                <span className="font-semibold text-sky-900">
                  {today.visits_approved} of {today.visits_total}
                </span>
              </div>
              {today.visits_total > today.visits_approved && (
                <p className="text-xs text-sky-700 pt-1 border-t border-sky-200">
                  Visits count towards TADA once the admin approves them — the claimable
                  figure will rise as approvals come through.
                </p>
              )}
            </div>
          )}

          {/* Punch action */}
          {nextAction ? (
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Home className="w-4 h-4 text-slate-400" />
                {nextAction === 'punch-in'
                  ? 'Punch in from your residence before leaving for the market'
                  : 'Punch out from your residence after reaching home'}
              </div>
              {/* key forces a fresh capture widget for each punch */}
              <GeoLocationCapture key={nextAction} onLocationCapture={setLocation} required />
              <Button
                onClick={() => punch(nextAction)}
                disabled={submitting || !location}
                className={cn(
                  'w-full h-12 rounded-xl text-white shadow-lg',
                  nextAction === 'punch-in'
                    ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'
                    : 'bg-sky-600 hover:bg-sky-700 shadow-sky-200'
                )}
                data-testid={`${nextAction}-btn`}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : nextAction === 'punch-in' ? (
                  <LogIn className="w-4 h-4 mr-2" />
                ) : (
                  <LogOut className="w-4 h-4 mr-2" />
                )}
                {nextAction === 'punch-in' ? 'Punch In' : 'Punch Out'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-emerald-700">
                Day complete — {today.attendance?.distance_km ?? today.distance_km} km,{' '}
                {today.attendance?.travel_type || today.travel_type}. TADA can now be claimed.
              </span>
            </div>
          )}
        </div>

        {/* Super admin: clear a punch made by mistake so the user can punch again */}
        {isSuperAdmin && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3"
               data-testid="remove-punch-panel">
            <div>
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-sky-600" />
                Remove wrong punch
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Today: the user punches in or out again with a fresh GPS location.
                Past day: you enter the correct punch out time. Removing a punch in clears the whole day.
                TADA: take a day that was not worked out of TADA without touching its punches.
              </p>
            </div>
            <div className="flex gap-2">
              <input
                type="date"
                value={teamDate}
                max={today?.date}
                onChange={(e) => {
                  if (!e.target.value) return;
                  setTeamDate(e.target.value);
                  fetchTeam(e.target.value);
                }}
                className="h-10 px-3 rounded-xl border border-slate-200 text-sm"
                data-testid="remove-punch-date"
              />
              <input
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                placeholder="Search user"
                className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-slate-200 text-sm"
                data-testid="remove-punch-search"
              />
            </div>
            {teamToday.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                Nobody punched on {teamDate ? formatDate(teamDate) : 'this day'}
              </p>
            ) : (
              <div className="space-y-2">
                {teamToday
                  .filter((row) => (row.user_name || '').toLowerCase()
                    .includes(teamSearch.trim().toLowerCase()))
                  .map((row) => (
                    <div key={row.id}
                         className="flex items-center justify-between gap-2 p-3 rounded-xl border border-slate-100"
                         data-testid={`remove-punch-row-${row.id}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{row.user_name}</p>
                        <p className="text-xs text-slate-500">
                          In {formatTime(row.punch_in?.time)} · Out {formatTime(row.punch_out?.time)}
                          {row.punch_out?.auto ? ' (auto)' : row.punch_out?.corrected ? ' (corrected)' : ''}
                          {row.tada_excluded && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium"
                                  title={row.tada_excluded_reason || ''}>
                              No TADA
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {row.punch_in && (
                          <Button size="sm" variant="outline"
                                  className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => openRemoval(row, 'in')}
                                  data-testid={`remove-punch-in-${row.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> In
                          </Button>
                        )}
                        {row.punch_out && (
                          <Button size="sm" variant="outline"
                                  className="rounded-lg text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => openRemoval(row, 'out')}
                                  data-testid={`remove-punch-out-${row.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Out
                          </Button>
                        )}
                        {row.punch_in && (row.tada_excluded ? (
                          <Button size="sm" variant="outline"
                                  className="rounded-lg text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                  onClick={() => openTada(row)}
                                  data-testid={`restore-tada-${row.id}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> TADA
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline"
                                  className="rounded-lg text-violet-700 border-violet-200 hover:bg-violet-50"
                                  onClick={() => openTada(row)}
                                  data-testid={`remove-tada-${row.id}`}>
                            <Receipt className="w-3 h-3 mr-1" /> TADA
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        <Dialog open={Boolean(removal)} onOpenChange={(open) => !open && setRemoval(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {correctingPastOut ? 'Correct' : 'Remove'} punch {removal?.kind} for{' '}
                {removal?.row.user_name} on {removal && formatDate(removal.row.date)}?
              </DialogTitle>
              <DialogDescription>
                {removal?.kind === 'in'
                  ? (removal?.row.date === today?.date
                    ? 'This clears the whole day, including any punch out. The user must punch in again with GPS before doing any field work.'
                    : "This clears the whole day, including the punch out and its automatic TADA claim. The user cannot punch for a past day, so it stays empty.")
                  : correctingPastOut
                    ? "The punch out is replaced by one at the time below, at the user's last visit before then (or their punch in point). The day's distance and TADA are recalculated."
                    : "This reopens the day. The day's automatic TADA claim is withdrawn and raised again when the user punches out with GPS."}
              </DialogDescription>
            </DialogHeader>
            {correctingPastOut && (
              <div className="space-y-2">
                <Label>Correct punch out time *</Label>
                <input
                  type="time"
                  value={correctedOut}
                  onChange={(e) => setCorrectedOut(e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm"
                  data-testid="remove-punch-time"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                placeholder="e.g. punched out by mistake at 11:00"
                data-testid="remove-punch-reason"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRemoval(null)} disabled={removing}>
                Cancel
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={confirmRemoval} disabled={removing}
                      data-testid="remove-punch-confirm">
                {removing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {correctingPastOut ? 'Save punch out' : `Remove punch ${removal?.kind}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(tadaRow)} onOpenChange={(open) => !open && setTadaRow(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {tadaRow?.tada_excluded ? 'Restore' : 'Remove'} TADA for {tadaRow?.user_name} on{' '}
                {tadaRow && formatDate(tadaRow.date)}?
              </DialogTitle>
              <DialogDescription>
                {tadaRow?.tada_excluded
                  ? 'The day goes back under TADA and its automatic claim is raised again from the journey.'
                  : "The punches stay as they are. The day's automatic TADA claim is deleted and will not be raised again, and the user cannot claim Headquarter or Ex-Station TADA for it."}
              </DialogDescription>
            </DialogHeader>
            {tadaRow?.tada_excluded && tadaRow?.tada_excluded_reason && (
              <p className="text-sm text-slate-600">
                Removed by {tadaRow.tada_excluded_by || 'admin'}: {tadaRow.tada_excluded_reason}
              </p>
            )}
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                value={tadaReason}
                onChange={(e) => setTadaReason(e.target.value)}
                placeholder={tadaRow?.tada_excluded ? 'e.g. removed by mistake' : 'e.g. Sunday - no field work'}
                data-testid="tada-reason"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setTadaRow(null)} disabled={tadaSaving}>
                Cancel
              </Button>
              <Button className={tadaRow?.tada_excluded
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-violet-600 hover:bg-violet-700 text-white'}
                      onClick={confirmTada} disabled={tadaSaving}
                      data-testid="tada-confirm">
                {tadaSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {tadaRow?.tada_excluded ? 'Restore TADA' : 'Remove TADA'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History */}
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Recent days
          </h2>
          <div className="space-y-2">
            {history.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
                <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No attendance recorded yet</p>
              </div>
            ) : (
              history.map((row) => {
                const incomplete = !row.punch_out;
                return (
                  <div
                    key={row.id}
                    className="bg-white rounded-xl border border-slate-100 p-4"
                    data-testid={`attendance-row-${row.date}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{formatDate(row.date)}</p>
                        <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {formatTime(row.punch_in?.time)} → {formatTime(row.punch_out?.time)}
                        </p>
                      </div>
                      <div className="text-right">
                        {incomplete ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            No punch out
                          </span>
                        ) : (
                          <>
                            <p className="font-semibold text-slate-900">{row.distance_km} km</p>
                            <p className="text-xs text-slate-500">{row.travel_type}</p>
                          </>
                        )}
                      </div>
                    </div>
                    {row.punch_in?.device_id && (
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-2">
                        <Smartphone className="w-3 h-3" />
                        {row.punch_in.device_id}
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Attendance;
